
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { studentAPI, adminAPI, facultyAPI, authAPI } from '../services/api';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [dashboardData, setDashboardData] = useState({});
  const [error, setError] = useState(null);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'viewEnrollment', 'addStudent', 'editStudent', 'addFaculty', 'editFaculty', 'credentials'
  const [selectedItem, setSelectedItem] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [attendanceStates, setAttendanceStates] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  
  // Marks form state
  const [marksForm, setMarksForm] = useState({
    student_id: '',
    subject: '',
    exam_type: 'midterm',
    marks_obtained: '',
    total_marks: '100',
    academic_year: '2025-2026',
    term: 'Term 1',
    remarks: ''
  });
  
  // Store created credentials for display
  const [createdCredentials, setCreatedCredentials] = useState(null);
  
  // Form states for adding/editing
  const [studentForm, setStudentForm] = useState({
    student_id: '', first_name: '', last_name: '', email: '', password: '', grade: '', section: '', phone: '', address: '', parent_name: '', parent_phone: ''
  });
  const [facultyForm, setFacultyForm] = useState({
    faculty_id: '', first_name: '', last_name: '', email: '', password: '', department: '', designation: '', phone: '', subject: '', qualification: ''
  });
  
  // Fee form state
  const [feeForm, setFeeForm] = useState({
    student_id: '',
    fee_type: 'tuition',
    amount: '',
    due_date: '',
    academic_year: '2025-2026',
    term: 'Term 1',
    description: ''
  });

  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Toast notifications
  const [toasts, setToasts] = useState([]);
  const showToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  };
  const dismissToast = (id) => setToasts(prev => prev.filter(t => t.id !== id));

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState({ show: false, title: '', message: '', onConfirm: null, type: 'danger' });
  const showConfirmDialog = (title, message, onConfirm, type = 'danger') => {
    setConfirmDialog({ show: true, title, message, onConfirm, type });
  };
  const closeConfirmDialog = () => setConfirmDialog({ show: false, title: '', message: '', onConfirm: null, type: 'danger' });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    
    // Fetch data based on user role
    if (userData.role) {
      fetchDashboardData(userData.role);
    } else {
      setLoading(false);
    }
  }, []);

  // initialize attendanceStates when students list updates
  useEffect(() => {
    if (dashboardData.students && dashboardData.students.length > 0) {
      const map = {};
      dashboardData.students.forEach((st, idx) => {
        map[st.id] = st.attendanceStatus || (idx % 6 === 0 ? 'absent' : 'present');
      });
      setAttendanceStates(map);
    }
  }, [dashboardData.students]);

  const markPresent = (studentId) => {
    setAttendanceStates(prev => ({ ...prev, [studentId]: 'present' }));
    // TODO: call backend API to persist attendance when available
  };

  const markAbsent = (studentId) => {
    setAttendanceStates(prev => ({ ...prev, [studentId]: 'absent' }));
    // TODO: call backend API to persist attendance when available
  };

  const fetchDashboardData = async (role) => {
    setLoading(true);
    setError(null);
    try {
      switch (role) {
        case 'student':
          const [studentProfile, results, fees] = await Promise.all([
            studentAPI.getProfile().catch(() => null),
            studentAPI.getResults().catch(() => []),
            studentAPI.getFees().catch(() => null)
          ]);
          setProfile(studentProfile);
          setDashboardData({ results, fees });
          break;
        case 'admin':
          const [students, faculty, allFees, enrollments, adminResults] = await Promise.all([
            adminAPI.getStudents().catch(() => []),
            adminAPI.getFaculty().catch(() => []),
            adminAPI.getFees().catch(() => []),
            adminAPI.getEnrollments().catch(() => []),
            adminAPI.getResults().catch(() => [])
          ]);
          setDashboardData({ students, faculty, fees: allFees, enrollments, results: adminResults });
          break;
        case 'faculty':
          const [facultyProfile, facultyStudents] = await Promise.all([
            facultyAPI.getProfile().catch(() => null),
            facultyAPI.getStudents().catch(() => [])
          ]);
          setProfile(facultyProfile);
          setDashboardData({ students: facultyStudents });
          break;
        default:
          break;
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  // ===== FEE HANDLER =====
  const handleSubmitFee = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      await adminAPI.createFee({
        student_id: feeForm.student_id,
        fee_type: feeForm.fee_type,
        amount: parseFloat(feeForm.amount),
        due_date: feeForm.due_date,
        academic_year: feeForm.academic_year,
        term: feeForm.term,
        description: feeForm.description
      });
      showToast('Fee assigned successfully!', 'success');
      setShowModal(false);
      setFeeForm({ student_id: '', fee_type: 'tuition', amount: '', due_date: '', academic_year: '2025-2026', term: 'Term 1', description: '' });
      await fetchDashboardData(user?.role);
    } catch (err) {
      showToast('Failed to assign fee: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // ===== ENROLLMENT HANDLERS =====
  const handleViewEnrollment = (enrollment) => {
    setSelectedItem(enrollment);
    setModalType('viewEnrollment');
    setShowModal(true);
  };

  const handleUpdateEnrollmentStatus = async (enrollmentId, status) => {
    if (status === 'rejected') {
      showConfirmDialog(
        'Reject Application',
        'Are you sure you want to reject this application? The enrollment will be permanently deleted.',
        async () => {
          closeConfirmDialog();
          setActionLoading(true);
          try {
            await adminAPI.deleteEnrollment(enrollmentId);
            await fetchDashboardData(user?.role);
            setShowModal(false);
            showToast('Application rejected and removed successfully!', 'success');
          } catch (err) {
            console.error('Error rejecting enrollment:', err);
            showToast('Failed to reject enrollment', 'error');
          } finally {
            setActionLoading(false);
          }
        },
        'danger'
      );
      return;
    }
    setActionLoading(true);
    try {
      await adminAPI.updateEnrollment(enrollmentId, { status });
      await fetchDashboardData(user?.role);
      setShowModal(false);
      showToast(`Application ${status} successfully!`, 'success');
    } catch (err) {
      console.error('Error updating enrollment:', err);
      showToast('Failed to update enrollment status', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteEnrollment = (enrollmentId) => {
    showConfirmDialog(
      'Delete Enrollment',
      'Are you sure you want to delete this enrollment application? This action cannot be undone.',
      async () => {
        closeConfirmDialog();
        setActionLoading(true);
        try {
          await adminAPI.deleteEnrollment(enrollmentId);
          await fetchDashboardData(user?.role);
          showToast('Enrollment deleted successfully!', 'success');
        } catch (err) {
          console.error('Error deleting enrollment:', err);
          showToast('Failed to delete enrollment', 'error');
        } finally {
          setActionLoading(false);
        }
      }
    );
  };

  // ===== STUDENT HANDLERS =====
  const handleAddStudent = () => {
    setStudentForm({ student_id: '', first_name: '', last_name: '', email: '', password: '', grade: '', section: '', phone: '', address: '', parent_name: '', parent_phone: '' });
    setModalType('addStudent');
    setShowModal(true);
  };

  const handleEditStudent = (student) => {
    setSelectedItem(student);
    setStudentForm({
      student_id: student.student_id || '',
      first_name: student.first_name || '',
      last_name: student.last_name || '',
      email: student.email || '',
      password: '',
      grade: student.grade || '',
      section: student.section || '',
      phone: student.phone || '',
      address: student.address || '',
      parent_name: student.parent_name || '',
      parent_phone: student.parent_phone || ''
    });
    setModalType('editStudent');
    setShowModal(true);
  };

  const handleSubmitStudent = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      if (modalType === 'addStudent') {
        await adminAPI.createStudent(studentForm);
        setCreatedCredentials({
          type: 'Student',
          name: `${studentForm.first_name} ${studentForm.last_name}`,
          email: studentForm.email,
          password: studentForm.password,
          id: studentForm.student_id
        });
        setModalType('credentials');
        await fetchDashboardData(user?.role);
      } else {
        const updateData = { ...studentForm };
        if (!updateData.password) delete updateData.password;
        await adminAPI.updateStudent(selectedItem.id, updateData);
        showToast('Student updated successfully!', 'success');
        await fetchDashboardData(user?.role);
        setShowModal(false);
      }
    } catch (err) {
      console.error('Error saving student:', err);
      showToast('Failed to save student: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteStudent = (studentId) => {
    showConfirmDialog(
      'Delete Student',
      'Are you sure you want to delete this student? This action cannot be undone.',
      async () => {
        closeConfirmDialog();
        setActionLoading(true);
        try {
          await adminAPI.deleteStudent(studentId);
          await fetchDashboardData(user?.role);
          showToast('Student deleted successfully!', 'success');
        } catch (err) {
          console.error('Error deleting student:', err);
          showToast('Failed to delete student', 'error');
        } finally {
          setActionLoading(false);
        }
      }
    );
  };

  // ===== FACULTY HANDLERS =====
  const handleAddFaculty = () => {
    setFacultyForm({ faculty_id: '', first_name: '', last_name: '', email: '', password: '', department: '', designation: '', phone: '', subject: '', qualification: '' });
    setModalType('addFaculty');
    setShowModal(true);
  };

  const handleEditFaculty = (faculty) => {
    setSelectedItem(faculty);
    setFacultyForm({
      faculty_id: faculty.faculty_id || '',
      first_name: faculty.first_name || '',
      last_name: faculty.last_name || '',
      email: faculty.email || '',
      password: '',
      department: faculty.department || '',
      designation: faculty.designation || '',
      phone: faculty.phone || '',
      subject: faculty.subject || '',
      qualification: faculty.qualification || ''
    });
    setModalType('editFaculty');
    setShowModal(true);
  };

  const handleSubmitFaculty = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      if (modalType === 'addFaculty') {
        await adminAPI.createFaculty(facultyForm);
        setCreatedCredentials({
          type: 'Faculty',
          name: `${facultyForm.first_name} ${facultyForm.last_name}`,
          email: facultyForm.email,
          password: facultyForm.password,
          id: facultyForm.faculty_id
        });
        setModalType('credentials');
        await fetchDashboardData(user?.role);
      } else {
        const updateData = { ...facultyForm };
        if (!updateData.password) delete updateData.password;
        await adminAPI.updateFaculty(selectedItem.id, updateData);
        showToast('Faculty updated successfully!', 'success');
        await fetchDashboardData(user?.role);
        setShowModal(false);
      }
    } catch (err) {
      console.error('Error saving faculty:', err);
      showToast('Failed to save faculty: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteFaculty = (facultyId) => {
    showConfirmDialog(
      'Delete Faculty',
      'Are you sure you want to delete this faculty member? This action cannot be undone.',
      async () => {
        closeConfirmDialog();
        setActionLoading(true);
        try {
          await adminAPI.deleteFaculty(facultyId);
          await fetchDashboardData(user?.role);
          showToast('Faculty deleted successfully!', 'success');
        } catch (err) {
          console.error('Error deleting faculty:', err);
          showToast('Failed to delete faculty', 'error');
        } finally {
          setActionLoading(false);
        }
      }
    );
  };

  // Handle saving marks from faculty dashboard
  const handleSaveMarks = async () => {
    if (!marksForm.student_id || !marksForm.subject || !marksForm.marks_obtained || !marksForm.total_marks) {
      showToast('Please fill all required fields', 'warning');
      return;
    }
    
    setActionLoading(true);
    try {
      await facultyAPI.addResult({
        student_id: parseInt(marksForm.student_id),
        subject: marksForm.subject,
        exam_type: marksForm.exam_type,
        marks_obtained: parseFloat(marksForm.marks_obtained),
        total_marks: parseFloat(marksForm.total_marks),
        academic_year: marksForm.academic_year,
        term: marksForm.term,
        remarks: marksForm.remarks || null
      });
      showToast('Marks saved successfully!', 'success');
      // Reset form
      setMarksForm({
        student_id: '',
        subject: '',
        exam_type: 'midterm',
        marks_obtained: '',
        total_marks: '100',
        academic_year: '2025-2026',
        term: 'Term 1',
        remarks: ''
      });
    } catch (err) {
      console.error('Error saving marks:', err);
      showToast('Failed to save marks: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle archive/deactivate student
  const handleArchiveStudent = (studentId) => {
    showConfirmDialog(
      'Archive Student',
      'Are you sure you want to archive this student? Their record will be retained.',
      async () => {
        closeConfirmDialog();
        setActionLoading(true);
        try {
          await adminAPI.updateStudent(studentId, { is_active: false });
          await fetchDashboardData(user?.role);
          showToast('Student archived successfully! Record retained.', 'success');
        } catch (err) {
          console.error('Error archiving student:', err);
          showToast('Failed to archive student', 'error');
        } finally {
          setActionLoading(false);
        }
      },
      'warning'
    );
  };

  // Handle archive/deactivate faculty
  const handleArchiveFaculty = (facultyId) => {
    showConfirmDialog(
      'Archive Faculty',
      'Are you sure you want to archive this faculty member? Their record will be retained.',
      async () => {
        closeConfirmDialog();
        setActionLoading(true);
        try {
          await adminAPI.updateFaculty(facultyId, { is_active: false });
          await fetchDashboardData(user?.role);
          showToast('Faculty archived successfully! Record retained.', 'success');
        } catch (err) {
          console.error('Error archiving faculty:', err);
          showToast('Failed to archive faculty', 'error');
        } finally {
          setActionLoading(false);
        }
      },
      'warning'
    );
  };

  // Handle assign fee to a specific student
  const handleAssignFee = (student) => {
    setFeeForm({ ...feeForm, student_id: student.id });
    setSelectedItem(student);
    setModalType('assignFee');
    setShowModal(true);
  };

  // Download Roll Number Slip as PDF
  const downloadRollSlip = () => {
    try {
      const slipContent = `
        <html>
        <head>
          <title>Roll Number Slip - ${profile?.first_name} ${profile?.last_name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; }
            .header { text-align: center; border-bottom: 2px solid #8b0000; padding-bottom: 20px; margin-bottom: 30px; }
            .header h1 { color: #8b0000; margin: 0; }
            .header p { color: #666; margin: 5px 0 0; }
            .logo { font-size: 48px; margin-bottom: 10px; }
            .slip-body { margin: 30px 0; }
            .row { display: flex; margin-bottom: 15px; border-bottom: 1px solid #eee; padding-bottom: 10px; }
            .label { font-weight: bold; width: 150px; color: #333; }
            .value { color: #666; }
            .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; font-size: 12px; color: #999; }
            .stamp { margin-top: 50px; text-align: right; }
            .stamp-text { border-top: 1px solid #333; padding-top: 10px; display: inline-block; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">🎓</div>
            <h1>MTC School System</h1>
            <p>Roll Number Slip - Academic Year 2025-2026</p>
          </div>
          <div class="slip-body">
            <div class="row"><span class="label">Name:</span><span class="value">${profile?.first_name} ${profile?.last_name}</span></div>
            <div class="row"><span class="label">Roll Number:</span><span class="value">${profile?.student_id}</span></div>
            <div class="row"><span class="label">Grade:</span><span class="value">${profile?.grade}</span></div>
            <div class="row"><span class="label">Section:</span><span class="value">${profile?.section}</span></div>
            <div class="row"><span class="label">Parent Name:</span><span class="value">${profile?.parent_name || 'N/A'}</span></div>
            <div class="row"><span class="label">Contact:</span><span class="value">${profile?.phone || 'N/A'}</span></div>
          </div>
          <div class="stamp">
            <div class="stamp-text">Principal's Signature</div>
          </div>
          <div class="footer">
            <p>This is a computer generated document - MTC School Management System</p>
            <p>Generated on: ${new Date().toLocaleDateString()}</p>
          </div>
        </body>
        </html>
      `;
      
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        showToast('Please allow popups for this website to print the slip.', 'warning');
        return;
      }
      printWindow.document.write(slipContent);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
    } catch (error) {
      console.error('Error generating roll slip:', error);
      showToast('Failed to generate roll slip. Please try again.', 'error');
    }
  };

  // Download Fee Challan
  const downloadChallan = () => {
    try {
      const challanContent = `
        <html>
        <head>
          <title>Fee Challan - ${profile?.first_name} ${profile?.last_name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; }
            .header { text-align: center; border-bottom: 2px solid #8b0000; padding-bottom: 20px; margin-bottom: 30px; }
            .header h1 { color: #8b0000; margin: 0; }
            .header p { color: #666; margin: 5px 0 0; }
            .logo { font-size: 48px; margin-bottom: 10px; }
            .challan-body { margin: 30px 0; }
            .row { display: flex; margin-bottom: 15px; border-bottom: 1px solid #eee; padding-bottom: 10px; }
            .label { font-weight: bold; width: 200px; color: #333; }
            .value { color: #666; }
            .total { background: #f8f9fa; padding: 15px; border-radius: 5px; margin-top: 20px; }
            .total .row { border-bottom: none; margin-bottom: 0; }
            .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; font-size: 12px; color: #999; }
            .stamp { margin-top: 50px; text-align: right; }
            .stamp-text { border-top: 1px solid #333; padding-top: 10px; display: inline-block; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">🎓</div>
            <h1>MTC School System</h1>
            <p>Fee Challan - Academic Year 2025-2026</p>
          </div>
          <div class="challan-body">
            <div class="row"><span class="label">Student Name:</span><span class="value">${profile?.first_name} ${profile?.last_name}</span></div>
            <div class="row"><span class="label">Roll Number:</span><span class="value">${profile?.student_id}</span></div>
            <div class="row"><span class="label">Grade:</span><span class="value">${profile?.grade}</span></div>
            <div class="row"><span class="label">Section:</span><span class="value">${profile?.section}</span></div>
            <div class="row"><span class="label">Parent Name:</span><span class="value">${profile?.parent_name || 'N/A'}</span></div>
            <div class="row"><span class="label">Contact:</span><span class="value">${profile?.phone || 'N/A'}</span></div>
            
            <div class="total">
              <h3>Fee Breakdown</h3>
              <div class="row"><span class="label">Tuition Fee:</span><span class="value">Rs. 50,000</span></div>
              <div class="row"><span class="label">Lab Fee:</span><span class="value">Rs. 15,000</span></div>
              <div class="row"><span class="label">Exam Fee:</span><span class="value">Rs. 10,000</span></div>
              <div class="row"><span class="label">Library Fee:</span><span class="value">Rs. 10,000</span></div>
              <div class="row" style="border-top: 2px solid #333; margin-top: 10px; padding-top: 10px;"><span class="label" style="font-size: 16px;">Total Amount:</span><span class="value" style="font-size: 16px; font-weight: bold;">Rs. 85,000</span></div>
            </div>
          </div>
          <div class="stamp">
            <div class="stamp-text">Accountant Signature</div>
          </div>
          <div class="footer">
            <p>This is a computer generated document - MTC School Management System</p>
            <p>Generated on: ${new Date().toLocaleDateString()}</p>
            <p>Please pay the fees by the due date to avoid late charges.</p>
          </div>
        </body>
        </html>
      `;
      
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        showToast('Please allow popups for this website to download the challan.', 'warning');
        return;
      }
      printWindow.document.write(challanContent);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
    } catch (error) {
      console.error('Error generating fee challan:', error);
      showToast('Failed to generate fee challan. Please try again.', 'error');
    }
  };

  // Filter functions
  const getFilteredStudents = () => {
    if (!searchTerm) return dashboardData.students || [];
    return (dashboardData.students || []).filter(s => 
      `${s.first_name} ${s.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.student_id?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getFilteredFaculty = () => {
    if (!searchTerm) return dashboardData.faculty || [];
    return (dashboardData.faculty || []).filter(f => 
      `${f.first_name} ${f.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.department?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Role-specific menu items
  const getMenuItems = () => {
    const role = user?.role;
    
    if (role === 'admin') {
      return [
        { id: 'dashboard', label: 'Dashboard', icon: 'fa-th-large' },
        { id: 'enrollments', label: 'Enrollments', icon: 'fa-file-signature' },
        { id: 'students', label: 'Manage Students', icon: 'fa-user-graduate' },
        { id: 'faculty', label: 'Manage Faculty', icon: 'fa-chalkboard-teacher' },
        { id: 'fees', label: 'Fee Management', icon: 'fa-money-bill-wave' },
        { id: 'results', label: 'Results', icon: 'fa-chart-bar' },
        { id: 'settings', label: 'Settings', icon: 'fa-cog' },
      ];
    } else if (role === 'faculty') {
      return [
        { id: 'dashboard', label: 'Dashboard', icon: 'fa-th-large' },
        { id: 'students', label: 'My Students', icon: 'fa-user-graduate' },
        { id: 'marks', label: 'Enter Marks', icon: 'fa-edit' },
        { id: 'attendance', label: 'Attendance', icon: 'fa-clipboard-check' },
        { id: 'schedule', label: 'My Schedule', icon: 'fa-calendar-alt' },
        { id: 'profile', label: 'My Profile', icon: 'fa-user' },
      ];
    } else {
      // Student menu
      return [
        { id: 'dashboard', label: 'Dashboard', icon: 'fa-th-large' },
        { id: 'rollno', label: 'Roll No Slip', icon: 'fa-id-card' },
        { id: 'courses', label: 'Registered Courses', icon: 'fa-book-open' },
        { id: 'timetable', label: 'Timetable', icon: 'fa-calendar-alt' },
        { id: 'attendance', label: 'Attendance Report', icon: 'fa-clipboard-check' },
        { id: 'grades', label: 'Grade Report', icon: 'fa-graduation-cap' },
        { id: 'marks', label: 'Marks Report', icon: 'fa-chart-bar' },
        { id: 'fee', label: 'Fee System', icon: 'fa-money-bill-wave' },
        { id: 'qec', label: 'QEC Feedback', icon: 'fa-comment-dots' },
      ];
    }
  };

  const menuItems = getMenuItems();

  // Get user display info based on role and profile
  const getUserInfo = () => {
    const role = user?.role;
    
    if (role === 'admin') {
      return {
        name: user?.email?.split('@')[0] || 'Admin',
        subtitle: 'Administrator',
        avatar: '👨‍💼'
      };
    } else if (role === 'faculty') {
      return {
        name: profile ? `${profile.first_name} ${profile.last_name}` : user?.email?.split('@')[0] || 'Faculty',
        subtitle: profile?.department || 'Faculty Member',
        avatar: '👨‍🏫'
      };
    } else {
      return {
        name: profile ? `${profile.first_name} ${profile.last_name}` : user?.email?.split('@')[0] || 'Student',
        subtitle: profile?.student_id || 'Student',
        avatar: '👨‍🎓'
      };
    }
  };

  const userInfo = getUserInfo();

  // Dashboard Stats based on role
  const getStats = () => {
    const role = user?.role;
    
    if (role === 'admin') {
      return [
        { label: 'Total Students', value: dashboardData.students?.length || '0', icon: 'fa-user-graduate', color: '#3b82f6' },
        { label: 'Total Faculty', value: dashboardData.faculty?.length || '0', icon: 'fa-chalkboard-teacher', color: '#22c55e' },
        { label: 'New Enrollments', value: dashboardData.enrollments?.filter(e => e.status === 'pending')?.length || '0', icon: 'fa-file-signature', color: '#f59e0b' },
        { label: 'All Enrollments', value: dashboardData.enrollments?.length || '0', icon: 'fa-users', color: '#8b5cf6' },
      ];
    } else if (role === 'faculty') {
      return [
        { label: 'Total Students', value: dashboardData.students?.length || '0', icon: 'fa-user-graduate', color: '#3b82f6' },
        { label: 'Classes Today', value: '4', icon: 'fa-chalkboard', color: '#22c55e' },
        { label: 'Pending Marks', value: '15', icon: 'fa-edit', color: '#f59e0b' },
        { label: 'Courses', value: '3', icon: 'fa-book', color: '#8b5cf6' },
      ];
    } else {
      return [
        { label: 'Subjects', value: dashboardData.results?.length || '0', icon: 'fa-book', color: '#3b82f6' },
        { label: 'Total Marks', value: dashboardData.results?.reduce((a,r) => a + (r.marks_obtained||0), 0) || '0', icon: 'fa-chart-line', color: '#22c55e' },
        { label: 'Total Fee', value: `Rs.${(dashboardData.fees?.total_fees || 0).toLocaleString()}`, icon: 'fa-money-bill', color: '#f59e0b' },
        { label: 'Pending Fee', value: `Rs.${(dashboardData.fees?.total_pending || 0).toLocaleString()}`, icon: 'fa-exclamation-circle', color: '#ef4444' },
      ];
    }
  };

  const stats = getStats();

  // Recent Activity
  const recentActivity = [
    { text: 'Marks updated for Mathematics', time: '2 hours ago', icon: 'fa-chart-line' },
    { text: 'Fee challan generated', time: '1 day ago', icon: 'fa-file-invoice' },
    { text: 'Attendance marked for Physics', time: '2 days ago', icon: 'fa-check-circle' },
    { text: 'New course registered', time: '1 week ago', icon: 'fa-plus-circle' },
  ];

  // Upcoming Schedule
  const schedule = [
    { subject: 'Mathematics', time: '09:00 AM', room: 'Room 101' },
    { subject: 'Physics', time: '11:00 AM', room: 'Lab 3' },
    { subject: 'English', time: '02:00 PM', room: 'Room 205' },
  ];

  const renderContent = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="error-container">
          <i className="fas fa-exclamation-triangle"></i>
          <p>{error}</p>
          <button onClick={() => fetchDashboardData(user?.role)}>Retry</button>
        </div>
      );
    }

    // Admin Dashboard Content
    if (user?.role === 'admin') {
      return renderAdminContent();
    }

    // Faculty Dashboard Content
    if (user?.role === 'faculty') {
      return renderFacultyContent();
    }

    // Student Dashboard Content (default)
    return renderStudentContent();
  };

  // Render Admin Dashboard
  const renderAdminContent = () => {
    switch (activeMenu) {
      case 'dashboard':
        return (
          <div className="dashboard-content">
            <div className="welcome-banner admin-banner">
              <div className="welcome-text">
                <h2>Welcome, Administrator! 👋</h2>
                <p>Manage your school efficiently from here.</p>
              </div>
              <div className="date-info">
                <span className="current-date">
                  <i className="fas fa-calendar"></i>
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
              </div>
            </div>

            <div className="stats-grid">
              {stats.map((stat, index) => (
                <div key={index} className="stat-card">
                  <div className="stat-icon" style={{ backgroundColor: `${stat.color}20`, color: stat.color }}>
                    <i className={`fas ${stat.icon}`}></i>
                  </div>
                  <div className="stat-info">
                    <h3>{stat.value}</h3>
                    <p>{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="dashboard-grid">
              <div className="dashboard-card">
                <div className="card-header">
                  <h3><i className="fas fa-user-graduate"></i> Recent Students</h3>
                </div>
                <div className="card-body">
                  {dashboardData.students?.slice(0, 5).map((student, index) => (
                    <div key={index} className="list-item">
                      <span className="avatar">👨‍🎓</span>
                      <div className="item-details">
                        <h4>{student.first_name} {student.last_name}</h4>
                        <span>{student.student_id} - Grade {student.grade}</span>
                      </div>
                    </div>
                  )) || <p>No students found</p>}
                </div>
              </div>

              <div className="dashboard-card">
                <div className="card-header">
                  <h3><i className="fas fa-chalkboard-teacher"></i> Faculty Members</h3>
                </div>
                <div className="card-body">
                  {dashboardData.faculty?.slice(0, 5).map((faculty, index) => (
                    <div key={index} className="list-item">
                      <span className="avatar">👨‍🏫</span>
                      <div className="item-details">
                        <h4>{faculty.first_name} {faculty.last_name}</h4>
                        <span>{faculty.department}</span>
                      </div>
                    </div>
                  )) || <p>No faculty found</p>}
                </div>
              </div>
            </div>
          </div>
        );
      case 'students':
        return (
          <div className="page-content">
            <h2><i className="fas fa-user-graduate"></i> Manage Students</h2>
            <div className="action-bar">
              <button className="add-btn" onClick={handleAddStudent}><i className="fas fa-plus"></i> Add Student</button>
              <input type="text" placeholder="Search students..." className="search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Grade</th>
                    <th>Section</th>
                    <th>Phone</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredStudents().length > 0 ? getFilteredStudents().map((student, index) => (
                    <tr key={index}>
                      <td>{student.student_id}</td>
                      <td>{student.first_name} {student.last_name}</td>
                      <td>{student.grade}</td>
                      <td>{student.section}</td>
                      <td>{student.phone || 'N/A'}</td>
                      <td>
                        <button className="action-btn edit" onClick={() => handleEditStudent(student)} disabled={actionLoading}><i className="fas fa-edit"></i></button>
                        <button className="action-btn archive" title="Archive Student" onClick={() => handleArchiveStudent(student.id)} disabled={actionLoading}><i className="fas fa-archive"></i></button>
                        <button className="action-btn delete" onClick={() => handleDeleteStudent(student.id)} disabled={actionLoading}><i className="fas fa-trash"></i></button>
                        <button className="action-btn fee" title="Assign Fee" onClick={() => handleAssignFee(student)} disabled={actionLoading}><i className="fas fa-file-invoice-dollar"></i></button>
                      </td>
                    </tr>
                  )) : <tr><td colSpan="6">No students found</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'faculty':
        return (
          <div className="page-content">
            <h2><i className="fas fa-chalkboard-teacher"></i> Manage Faculty</h2>
            <div className="action-bar">
              <button className="add-btn" onClick={handleAddFaculty}><i className="fas fa-plus"></i> Add Faculty</button>
              <input type="text" placeholder="Search faculty..." className="search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Department</th>
                    <th>Designation</th>
                    <th>Phone</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredFaculty().length > 0 ? getFilteredFaculty().map((faculty, index) => (
                    <tr key={index}>
                      <td>{faculty.faculty_id}</td>
                      <td>{faculty.first_name} {faculty.last_name}</td>
                      <td>{faculty.department}</td>
                      <td>{faculty.designation}</td>
                      <td>{faculty.phone || 'N/A'}</td>
                      <td>
                        <button className="action-btn edit" onClick={() => handleEditFaculty(faculty)} disabled={actionLoading}><i className="fas fa-edit"></i></button>
                        <button className="action-btn archive" title="Archive Faculty" onClick={() => handleArchiveFaculty(faculty.id)} disabled={actionLoading}><i className="fas fa-archive"></i></button>
                        <button className="action-btn delete" onClick={() => handleDeleteFaculty(faculty.id)} disabled={actionLoading}><i className="fas fa-trash"></i></button>
                      </td>
                    </tr>
                  )) : <tr><td colSpan="6">No faculty found</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'enrollments':
        return (
          <div className="page-content">
            <h2><i className="fas fa-file-signature"></i> Enrollment Applications</h2>
            <div className="enrollment-summary">
              <div className="stat-card-small pending">
                <span>{dashboardData.enrollments?.filter(e => e.status === 'pending')?.length || 0}</span>
                <p>Pending</p>
              </div>
              <div className="stat-card-small reviewed">
                <span>{dashboardData.enrollments?.filter(e => e.status === 'reviewed')?.length || 0}</span>
                <p>Reviewed</p>
              </div>
              <div className="stat-card-small approved">
                <span>{dashboardData.enrollments?.filter(e => e.status === 'approved')?.length || 0}</span>
                <p>Approved</p>
              </div>
              <div className="stat-card-small rejected">
                <span>{dashboardData.enrollments?.filter(e => e.status === 'rejected')?.length || 0}</span>
                <p>Rejected</p>
              </div>
            </div>
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Student Name</th>
                    <th>Father's Name</th>
                    <th>Class</th>
                    <th>Phone</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.enrollments?.length > 0 ? dashboardData.enrollments.map((enrollment, index) => (
                    <tr key={index}>
                      <td>#{enrollment.id}</td>
                      <td>{enrollment.student_name}</td>
                      <td>{enrollment.father_name}</td>
                      <td>{enrollment.applying_for_class}</td>
                      <td>{enrollment.phone}</td>
                      <td>{new Date(enrollment.created_at).toLocaleDateString()}</td>
                      <td>
                        <span className={`badge ${enrollment.status}`}>
                          {enrollment.status.charAt(0).toUpperCase() + enrollment.status.slice(1)}
                        </span>
                      </td>
                      <td>
                        <button className="action-btn view" title="View Details" onClick={() => handleViewEnrollment(enrollment)}><i className="fas fa-eye"></i></button>
                        <button className="action-btn edit" title="Approve" onClick={() => handleUpdateEnrollmentStatus(enrollment.id, 'approved')} disabled={actionLoading}><i className="fas fa-check"></i></button>
                        <button className="action-btn delete" title="Reject" onClick={() => handleUpdateEnrollmentStatus(enrollment.id, 'rejected')} disabled={actionLoading}><i className="fas fa-times"></i></button>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="8" style={{textAlign: 'center', padding: '30px'}}>No enrollment applications yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'fees':
        return (
          <div className="page-content">
            <h2><i className="fas fa-money-bill-wave"></i> Fee Management</h2>
            <div className="fee-summary">
              <div className="fee-card total"><h4>Total Collection</h4><span>Rs. {dashboardData.fees?.reduce((acc, f) => acc + (f.amount_paid || 0), 0).toLocaleString() || '0'}</span></div>
              <div className="fee-card paid"><h4>Paid</h4><span>{dashboardData.fees?.filter(f => f.payment_status === 'paid')?.length || 0}</span></div>
              <div className="fee-card due"><h4>Pending</h4><span>{dashboardData.fees?.filter(f => f.payment_status === 'pending' || f.payment_status === 'partial')?.length || 0}</span></div>
            </div>
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Student ID</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Paid</th>
                    <th>Due Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.fees?.length > 0 ? dashboardData.fees.map((fee, index) => (
                    <tr key={index}>
                      <td>#{fee.id}</td>
                      <td>{fee.student_id}</td>
                      <td style={{textTransform:'capitalize'}}>{fee.fee_type}</td>
                      <td>Rs. {fee.amount?.toLocaleString()}</td>
                      <td>Rs. {fee.amount_paid?.toLocaleString()}</td>
                      <td>{fee.due_date}</td>
                      <td><span className={`badge ${fee.payment_status}`}>{fee.payment_status?.charAt(0).toUpperCase() + fee.payment_status?.slice(1)}</span></td>
                    </tr>
                  )) : <tr><td colSpan="7" style={{textAlign:'center',padding:'30px'}}>No fee records found</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'results':
        return (
          <div className="page-content">
            <h2><i className="fas fa-chart-bar"></i> Results Management</h2>
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Student ID</th>
                    <th>Subject</th>
                    <th>Exam</th>
                    <th>Marks</th>
                    <th>Grade</th>
                    <th>Year</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.results?.length > 0 ? dashboardData.results.map((r, index) => (
                    <tr key={index}>
                      <td>#{r.id}</td>
                      <td>{r.student_id}</td>
                      <td>{r.subject}</td>
                      <td style={{textTransform:'capitalize'}}>{r.exam_type}</td>
                      <td>{r.marks_obtained}/{r.total_marks}</td>
                      <td><span className={`grade-${r.grade?.toLowerCase()?.replace('+','')}`}>{r.grade}</span></td>
                      <td>{r.academic_year}</td>
                    </tr>
                  )) : <tr><td colSpan="7" style={{textAlign:'center',padding:'30px'}}>No results found</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'settings':
        return (
          <div className="page-content">
            <h2><i className="fas fa-cog"></i> Settings</h2>
            <div className="form-card" style={{maxWidth:'500px'}}>
              <h3 style={{marginBottom:'20px'}}>Change Password</h3>
              <div className="form-group">
                <label>Current Password *</label>
                <input type="password" value={passwordForm.current_password} onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>New Password *</label>
                <input type="password" value={passwordForm.new_password} onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Confirm New Password *</label>
                <input type="password" value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})} required />
              </div>
              <button className="submit-btn" disabled={actionLoading} onClick={async () => {
                if (!passwordForm.current_password || !passwordForm.new_password) {
                  showToast('Please fill all fields', 'warning'); return;
                }
                if (passwordForm.new_password !== passwordForm.confirm_password) {
                  showToast('New passwords do not match', 'warning'); return;
                }
                if (passwordForm.new_password.length < 6) {
                  showToast('Password must be at least 6 characters', 'warning'); return;
                }
                setActionLoading(true);
                try {
                  await authAPI.changePassword(passwordForm.current_password, passwordForm.new_password);
                  showToast('Password changed successfully!', 'success');
                  setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
                } catch (err) {
                  showToast('Failed: ' + (err.response?.data?.detail || err.message), 'error');
                } finally {
                  setActionLoading(false);
                }
              }}>
                <i className="fas fa-key"></i> {actionLoading ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </div>
        );
      default:
        return <div className="page-content"><h2>Select a menu item</h2></div>;
    }
  };

  // Render Faculty Dashboard
  const renderFacultyContent = () => {
    switch (activeMenu) {
      case 'dashboard':
        return (
          <div className="dashboard-content">
            <div className="welcome-banner faculty-banner">
              <div className="welcome-text">
                <h2>Welcome back, {userInfo.name}! 👋</h2>
                <p>Here's your teaching overview for today.</p>
              </div>
              <div className="date-info">
                <span className="current-date">
                  <i className="fas fa-calendar"></i>
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
              </div>
            </div>

            <div className="stats-grid">
              {stats.map((stat, index) => (
                <div key={index} className="stat-card">
                  <div className="stat-icon" style={{ backgroundColor: `${stat.color}20`, color: stat.color }}>
                    <i className={`fas ${stat.icon}`}></i>
                  </div>
                  <div className="stat-info">
                    <h3>{stat.value}</h3>
                    <p>{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="dashboard-grid">
              <div className="dashboard-card schedule-card">
                <div className="card-header">
                  <h3><i className="fas fa-calendar-day"></i> Today's Classes</h3>
                </div>
                <div className="card-body">
                  {schedule.map((item, index) => (
                    <div key={index} className="schedule-item">
                      <div className="schedule-time">
                        <i className="fas fa-clock"></i>
                        {item.time}
                      </div>
                      <div className="schedule-details">
                        <h4>{item.subject}</h4>
                        <span>{item.room}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="dashboard-card">
                <div className="card-header">
                  <h3><i className="fas fa-user-graduate"></i> My Students</h3>
                </div>
                <div className="card-body">
                  {dashboardData.students?.slice(0, 5).map((student, index) => (
                    <div key={index} className="list-item">
                      <span className="avatar">👨‍🎓</span>
                      <div className="item-details">
                        <h4>{student.first_name} {student.last_name}</h4>
                        <span>Grade {student.grade} - Section {student.section}</span>
                      </div>
                    </div>
                  )) || <p>No students assigned</p>}
                </div>
              </div>
            </div>
          </div>
        );
      case 'students':
        return (
          <div className="page-content">
            <h2><i className="fas fa-user-graduate"></i> My Students</h2>
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Grade</th>
                    <th>Section</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.students?.map((student, index) => (
                    <tr key={index}>
                      <td>{student.student_id}</td>
                      <td>{student.first_name} {student.last_name}</td>
                      <td>{student.grade}</td>
                      <td>{student.section}</td>
                      <td>
                        <button className="action-btn view"><i className="fas fa-eye"></i></button>
                        <button className="action-btn edit"><i className="fas fa-edit"></i> Add Marks</button>
                      </td>
                    </tr>
                  )) || <tr><td colSpan="5">No students found</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'marks':
        return (
          <div className="page-content">
            <h2><i className="fas fa-edit"></i> Enter Marks</h2>
            <div className="form-card">
              <div className="form-group">
                <label>Select Student *</label>
                <select value={marksForm.student_id} onChange={(e) => setMarksForm({...marksForm, student_id: e.target.value})} required>
                  <option value="">Select a student...</option>
                  {dashboardData.students?.map((student, index) => (
                    <option key={index} value={student.id}>{student.first_name} {student.last_name} ({student.student_id})</option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Subject *</label>
                  <input type="text" placeholder="Enter subject name" value={marksForm.subject} onChange={(e) => setMarksForm({...marksForm, subject: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>Exam Type *</label>
                  <select value={marksForm.exam_type} onChange={(e) => setMarksForm({...marksForm, exam_type: e.target.value})} required>
                    <option value="midterm">Midterm</option>
                    <option value="final">Final</option>
                    <option value="quiz">Quiz</option>
                    <option value="assignment">Assignment</option>
                    <option value="practical">Practical</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Marks Obtained *</label>
                  <input type="number" placeholder="0" min="0" value={marksForm.marks_obtained} onChange={(e) => setMarksForm({...marksForm, marks_obtained: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>Total Marks *</label>
                  <input type="number" placeholder="100" min="1" value={marksForm.total_marks} onChange={(e) => setMarksForm({...marksForm, total_marks: e.target.value})} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Academic Year</label>
                  <select value={marksForm.academic_year} onChange={(e) => setMarksForm({...marksForm, academic_year: e.target.value})}>
                    <option value="2025-2026">2025-2026</option>
                    <option value="2024-2025">2024-2025</option>
                    <option value="2026-2027">2026-2027</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Term</label>
                  <select value={marksForm.term} onChange={(e) => setMarksForm({...marksForm, term: e.target.value})}>
                    <option value="Term 1">Term 1</option>
                    <option value="Term 2">Term 2</option>
                    <option value="Term 3">Term 3</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Remarks</label>
                <textarea placeholder="Any remarks..." value={marksForm.remarks} onChange={(e) => setMarksForm({...marksForm, remarks: e.target.value})}></textarea>
              </div>
              <button className="submit-btn" onClick={handleSaveMarks} disabled={actionLoading}>
                <i className="fas fa-save"></i> {actionLoading ? 'Saving...' : 'Save Marks'}
              </button>
            </div>
          </div>
        );
      case 'profile':
        return (
          <div className="page-content">
            <h2><i className="fas fa-user"></i> My Profile</h2>
            <div className="profile-card">
              <div className="profile-header">
                <div className="profile-avatar large">👨‍🏫</div>
                <h3>{profile?.first_name} {profile?.last_name}</h3>
                <p>{profile?.subject} Teacher</p>
              </div>
              <div className="profile-details">
                <div className="detail-row"><span>Faculty ID:</span><strong>{profile?.faculty_id}</strong></div>
                <div className="detail-row"><span>Department:</span><strong>{profile?.department}</strong></div>
                <div className="detail-row"><span>Subject:</span><strong>{profile?.subject}</strong></div>
                <div className="detail-row"><span>Qualification:</span><strong>{profile?.qualification || 'N/A'}</strong></div>
                <div className="detail-row"><span>Phone:</span><strong>{profile?.phone || 'N/A'}</strong></div>
              </div>
            </div>
          </div>
        );
      case 'attendance':
        return (
          <div className="page-content">
            <h2><i className="fas fa-clipboard-check"></i> Student Attendance</h2>
            <div className="attendance-controls">
              <div className="form-row">
                <div className="form-group">
                  <label>Select Grade</label>
                  <select>
                    <option value="">All Grades</option>
                    {[1,2,3,4,5,6,7,8,9,10].map(g => <option key={g} value={g}>Grade {g}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Select Section</label>
                  <select>
                    <option value="">All Sections</option>
                    <option value="A">Section A</option>
                    <option value="B">Section B</option>
                    <option value="C">Section C</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" defaultValue={new Date().toISOString().split('T')[0]} />
                </div>
              </div>
            </div>
            <div className="attendance-summary">
              <div className="summary-card present">
                <i className="fas fa-check-circle"></i>
                <div>
                  <h4>Present</h4>
                  <span>{Object.values(attendanceStates).filter(s => s === 'present').length || 0}</span>
                </div>
              </div>
              <div className="summary-card absent">
                <i className="fas fa-times-circle"></i>
                <div>
                  <h4>Absent</h4>
                  <span>{Object.values(attendanceStates).filter(s => s === 'absent').length || 0}</span>
                </div>
              </div>
              <div className="summary-card total">
                <i className="fas fa-users"></i>
                <div>
                  <h4>Total</h4>
                  <span>{dashboardData.students?.length || 0}</span>
                </div>
              </div>
            </div>
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th>Student ID</th>
                    <th>Name</th>
                    <th>Grade</th>
                    <th>Section</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.students?.map((student, index) => (
                    <tr key={index}>
                      <td>{student.student_id}</td>
                      <td>{student.first_name} {student.last_name}</td>
                      <td>{student.grade}</td>
                      <td>{student.section}</td>
                      <td>
                        <span className={`status-badge ${attendanceStates[student.id] === 'absent' ? 'absent' : 'present'}`}>
                          {attendanceStates[student.id] === 'absent' ? 'Absent' : 'Present'}
                        </span>
                      </td>
                      <td>
                        <button className="action-btn present-btn" title="Mark Present" onClick={() => markPresent(student.id)}><i className="fas fa-check"></i></button>
                        <button className="action-btn absent-btn" title="Mark Absent" onClick={() => markAbsent(student.id)}><i className="fas fa-times"></i></button>
                      </td>
                    </tr>
                  )) || <tr><td colSpan="6">No students found</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'schedule':
        return (
          <div className="page-content">
            <h2><i className="fas fa-calendar-alt"></i> My Schedule</h2>
            <div className="schedule-grid">
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map((day, dayIndex) => (
                <div key={day} className="schedule-day">
                  <h3>{day}</h3>
                  <div className="day-classes">
                    {[
                      { time: '08:00 - 09:00', subject: profile?.subject || 'Physics', grade: 'Grade 10-A', room: 'Room 101' },
                      { time: '09:00 - 10:00', subject: profile?.subject || 'Physics', grade: 'Grade 9-B', room: 'Room 102' },
                      { time: '11:00 - 12:00', subject: profile?.subject || 'Physics', grade: 'Grade 8-A', room: 'Lab 1' },
                    ].slice(0, dayIndex === 4 ? 2 : 3).map((cls, idx) => (
                      <div key={idx} className="class-slot">
                        <span className="class-time">{cls.time}</span>
                        <strong className="class-subject">{cls.subject}</strong>
                        <span className="class-info">{cls.grade} | {cls.room}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      default:
        return <div className="page-content"><h2>Select a menu item</h2></div>;
    }
  };

  // Render Student Dashboard
  const renderStudentContent = () => {
    switch (activeMenu) {
      case 'dashboard':
        return (
          <div className="dashboard-content">
            <div className="welcome-banner">
              <div className="welcome-text">
                <h2>Welcome back, {userInfo.name}! 👋</h2>
                <p>Here's what's happening with your academics today.</p>
              </div>
              <div className="date-info">
                <span className="current-date">
                  <i className="fas fa-calendar"></i>
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
              </div>
            </div>

            <div className="stats-grid">
              {stats.map((stat, index) => (
                <div key={index} className="stat-card">
                  <div className="stat-icon" style={{ backgroundColor: `${stat.color}20`, color: stat.color }}>
                    <i className={`fas ${stat.icon}`}></i>
                  </div>
                  <div className="stat-info">
                    <h3>{stat.value}</h3>
                    <p>{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="dashboard-grid">
              <div className="dashboard-card schedule-card">
                <div className="card-header">
                  <h3><i className="fas fa-calendar-day"></i> Today's Schedule</h3>
                </div>
                <div className="card-body">
                  {schedule.map((item, index) => (
                    <div key={index} className="schedule-item">
                      <div className="schedule-time">
                        <i className="fas fa-clock"></i>
                        {item.time}
                      </div>
                      <div className="schedule-details">
                        <h4>{item.subject}</h4>
                        <span>{item.room}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="dashboard-card activity-card">
                <div className="card-header">
                  <h3><i className="fas fa-history"></i> Recent Activity</h3>
                </div>
                <div className="card-body">
                  {recentActivity.map((item, index) => (
                    <div key={index} className="activity-item">
                      <div className="activity-icon">
                        <i className={`fas ${item.icon}`}></i>
                      </div>
                      <div className="activity-details">
                        <p>{item.text}</p>
                        <span>{item.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      case 'rollno':
        return (
          <div className="page-content">
            <h2><i className="fas fa-id-card"></i> Roll No Slip</h2>
            <div className="roll-slip-card">
              <div className="slip-header">
                <h3>🎓 MTC School System</h3>
                <p>Roll Number Slip - Academic Year 2025-2026</p>
              </div>
              <div className="slip-body">
                <div className="slip-row"><span>Name:</span><strong>{profile?.first_name} {profile?.last_name}</strong></div>
                <div className="slip-row"><span>Roll Number:</span><strong>{profile?.student_id}</strong></div>
                <div className="slip-row"><span>Grade:</span><strong>{profile?.grade}</strong></div>
                <div className="slip-row"><span>Section:</span><strong>{profile?.section}</strong></div>
                <div className="slip-row"><span>Parent Name:</span><strong>{profile?.parent_name || 'N/A'}</strong></div>
                <div className="slip-row"><span>Contact:</span><strong>{profile?.phone || 'N/A'}</strong></div>
              </div>
              <button className="download-btn" onClick={downloadRollSlip}><i className="fas fa-download"></i> Download PDF</button>
            </div>
          </div>
        );
      case 'courses':
        return (
          <div className="page-content">
            <h2><i className="fas fa-book-open"></i> Registered Courses</h2>
            <div className="courses-table">
              <table>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Course Name</th>
                    <th>Credits</th>
                    <th>Instructor</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>MTH-301</td><td>Advanced Mathematics</td><td>3</td><td>Dr. Ahmed</td><td><span className="badge enrolled">Enrolled</span></td></tr>
                  <tr><td>PHY-302</td><td>Physics II</td><td>4</td><td>Dr. Khan</td><td><span className="badge enrolled">Enrolled</span></td></tr>
                  <tr><td>CSC-305</td><td>Data Structures</td><td>3</td><td>Prof. Ali</td><td><span className="badge enrolled">Enrolled</span></td></tr>
                  <tr><td>ENG-201</td><td>Technical Writing</td><td>2</td><td>Ms. Sarah</td><td><span className="badge enrolled">Enrolled</span></td></tr>
                  <tr><td>CSC-310</td><td>Database Systems</td><td>3</td><td>Dr. Usman</td><td><span className="badge pending">Pending</span></td></tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'timetable':
        return (
          <div className="page-content">
            <h2><i className="fas fa-calendar-alt"></i> Timetable</h2>
            <div className="timetable-grid">
              <div className="timetable-day">
                <h4>Monday</h4>
                <div className="class-slot">09:00 - Mathematics (Room 101)</div>
                <div className="class-slot">11:00 - Physics (Lab 3)</div>
                <div className="class-slot">14:00 - English (Room 205)</div>
              </div>
              <div className="timetable-day">
                <h4>Tuesday</h4>
                <div className="class-slot">10:00 - Data Structures (Lab 1)</div>
                <div className="class-slot">13:00 - Physics (Room 102)</div>
              </div>
              <div className="timetable-day">
                <h4>Wednesday</h4>
                <div className="class-slot">09:00 - Mathematics (Room 101)</div>
                <div className="class-slot">12:00 - Technical Writing (Room 203)</div>
              </div>
              <div className="timetable-day">
                <h4>Thursday</h4>
                <div className="class-slot">10:00 - Data Structures (Lab 1)</div>
                <div className="class-slot">14:00 - Physics Lab (Lab 3)</div>
              </div>
              <div className="timetable-day">
                <h4>Friday</h4>
                <div className="class-slot">09:00 - Mathematics (Room 101)</div>
                <div className="class-slot">11:00 - English (Room 205)</div>
              </div>
            </div>
          </div>
        );
      case 'attendance':
        return (
          <div className="page-content">
            <h2><i className="fas fa-clipboard-check"></i> Attendance Report</h2>
            <div className="attendance-summary">
              <div className="attendance-stat present"><span>85%</span><p>Present</p></div>
              <div className="attendance-stat absent"><span>10%</span><p>Absent</p></div>
              <div className="attendance-stat leave"><span>5%</span><p>Leave</p></div>
            </div>
            <div className="attendance-table">
              <table>
                <thead><tr><th>Subject</th><th>Total Classes</th><th>Present</th><th>Absent</th><th>Percentage</th></tr></thead>
                <tbody>
                  <tr><td>Mathematics</td><td>30</td><td>27</td><td>3</td><td>90%</td></tr>
                  <tr><td>Physics</td><td>28</td><td>24</td><td>4</td><td>86%</td></tr>
                  <tr><td>Data Structures</td><td>25</td><td>22</td><td>3</td><td>88%</td></tr>
                  <tr><td>English</td><td>20</td><td>16</td><td>4</td><td>80%</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'grades':
        return (
          <div className="page-content">
            <h2><i className="fas fa-graduation-cap"></i> Grade Report</h2>
            {/* CGPA display removed per request */}
            <div className="grades-table">
              <table>
                <thead><tr><th>Semester</th><th>Credits</th><th>GPA</th><th>Status</th></tr></thead>
                <tbody>
                  <tr><td>1st Semester</td><td>18</td><td>3.20</td><td><span className="badge enrolled">Completed</span></td></tr>
                  <tr><td>2nd Semester</td><td>19</td><td>3.45</td><td><span className="badge enrolled">Completed</span></td></tr>
                  <tr><td>3rd Semester</td><td>21</td><td>3.60</td><td><span className="badge enrolled">Completed</span></td></tr>
                  <tr><td>4th Semester</td><td>21</td><td>-</td><td><span className="badge pending">In Progress</span></td></tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'marks':
        return (
          <div className="page-content">
            <h2><i className="fas fa-chart-bar"></i> Marks Report</h2>
            <div className="marks-table">
              <table>
                <thead><tr><th>Subject</th><th>Exam Type</th><th>Marks</th><th>Grade</th><th>Year</th></tr></thead>
                <tbody>
                  {dashboardData.results?.length > 0 ? dashboardData.results.map((r, index) => (
                    <tr key={index}>
                      <td>{r.subject}</td>
                      <td style={{textTransform:'capitalize'}}>{r.exam_type}</td>
                      <td>{r.marks_obtained}/{r.total_marks}</td>
                      <td><span className={`grade-${r.grade?.toLowerCase()?.replace('+','')}`}>{r.grade || 'N/A'}</span></td>
                      <td>{r.academic_year}</td>
                    </tr>
                  )) : <tr><td colSpan="5" style={{textAlign:'center',padding:'30px'}}>No marks available yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'fee':
        return (
          <div className="page-content">
            <h2><i className="fas fa-money-bill-wave"></i> Fee System</h2>
            <div className="fee-summary">
              <div className="fee-card total"><h4>Total Fee</h4><span>Rs. {dashboardData.fees?.total_fees?.toLocaleString() || '0'}</span></div>
              <div className="fee-card paid"><h4>Paid</h4><span>Rs. {dashboardData.fees?.total_paid?.toLocaleString() || '0'}</span></div>
              <div className="fee-card due"><h4>Due</h4><span>Rs. {dashboardData.fees?.total_pending?.toLocaleString() || '0'}</span></div>
            </div>

            {/* Payment Information Card */}
            <div className="payment-info-card">
              <h3><i className="fas fa-mobile-alt"></i> Payment Methods</h3>
              <div className="payment-grid">
                <div className="payment-field">
                  <p className="payment-label">Account Title</p>
                  <p className="payment-value">Mahveer Kumar Lohia</p>
                </div>
                <div className="payment-field">
                  <p className="payment-label">EasyPaisa Number</p>
                  <p className="payment-value highlight">0300-1234567</p>
                </div>
                <div className="payment-field">
                  <p className="payment-label">JazzCash Number</p>
                  <p className="payment-value highlight">0301-1234567</p>
                </div>
              </div>
              <p className="payment-note">
                <i className="fas fa-info-circle"></i> Please include your Student ID ({profile?.student_id || 'N/A'}) in the payment reference
              </p>
            </div>

            {/* CEO Information Card */}
            <div className="ceo-contact-card">
              <h4><i className="fas fa-user-tie"></i> For Queries Contact</h4>
              <div className="ceo-profile">
                <div className="ceo-avatar">👨‍💼</div>
                <div className="ceo-details">
                  <p className="ceo-name">Mahveer Kumar Lohia</p>
                  <p className="ceo-title">CEO, MTC School System</p>
                  <p><i className="fas fa-phone"></i> 0321-9876543</p>
                  <p><i className="fas fa-envelope"></i> ceo@mtcschool.edu.pk</p>
                </div>
              </div>
            </div>

            <div className="fee-table">
              <table>
                <thead><tr><th>Description</th><th>Amount</th><th>Due Date</th><th>Status</th></tr></thead>
                <tbody>
                  {dashboardData.fees?.fees?.length > 0 ? dashboardData.fees.fees.map((fee, index) => (
                    <tr key={index}>
                      <td style={{textTransform:'capitalize'}}>{fee.fee_type} {fee.description ? `- ${fee.description}` : ''}</td>
                      <td>Rs. {fee.amount?.toLocaleString()}</td>
                      <td>{fee.due_date}</td>
                      <td><span className={`badge ${fee.payment_status === 'paid' ? 'enrolled' : 'pending'}`}>{fee.payment_status?.charAt(0).toUpperCase() + fee.payment_status?.slice(1)}</span></td>
                    </tr>
                  )) : <tr><td colSpan="4" style={{textAlign:'center',padding:'30px'}}>No fee records found</td></tr>}
                </tbody>
              </table>
            </div>
            <button className="download-btn" onClick={downloadChallan}><i className="fas fa-download"></i> Download Challan</button>
          </div>
        );
      case 'qec':
        return (
          <div className="page-content">
            <h2><i className="fas fa-comment-dots"></i> QEC Feedback</h2>
            <div className="qec-info">
              <p>Your feedback helps us improve! Please complete the course evaluation for each enrolled course.</p>
            </div>
            <div className="qec-list">
              <div className="qec-item"><span>Mathematics - Dr. Ahmed</span><button className="feedback-btn">Give Feedback</button></div>
              <div className="qec-item"><span>Physics - Dr. Khan</span><button className="feedback-btn">Give Feedback</button></div>
              <div className="qec-item"><span>Data Structures - Prof. Ali</span><button className="feedback-btn completed">Completed ✓</button></div>
              <div className="qec-item"><span>English - Ms. Sarah</span><button className="feedback-btn">Give Feedback</button></div>
            </div>
          </div>
        );
      default:
        return <div className="page-content"><h2>Select a menu item</h2></div>;
    }
  };

  // Get portal title based on role
  const getPortalTitle = () => {
    switch (user?.role) {
      case 'admin': return 'Admin Portal';
      case 'faculty': return 'Faculty Portal';
      default: return 'Student Portal';
    }
  };

  return (
    <section id="dashboard" className="dashboard-section">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="logo-area">
            <div className="logo-icon">🎓</div>
            <div className="logo-text">
              <h2>MTC</h2>
              <span>{getPortalTitle()}</span>
            </div>
          </div>
        </div>

        <div className="student-profile">
          <div className="profile-avatar">{userInfo.avatar}</div>
          <div className="profile-info">
            <h4>{userInfo.name}</h4>
            <p>{userInfo.subtitle}</p>
          </div>
        </div>

        <nav className="sidebar-nav">
          <ul className="menu-list">
            {menuItems.map((item) => (
              <li key={item.id}>
                <button
                  className={`menu-item ${activeMenu === item.id ? 'active' : ''}`}
                  onClick={() => setActiveMenu(item.id)}
                >
                  <i className={`fas ${item.icon}`}></i>
                  <span>{item.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="sidebar-footer">
          <Link to="/" className="home-link">
            <i className="fas fa-home"></i>
            <span>Home</span>
          </Link>
          <button className="logout-btn" onClick={handleLogout}>
            <i className="fas fa-sign-out-alt"></i>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <header className="top-header">
          <div className="search-bar">
            <i className="fas fa-search"></i>
            <input type="text" placeholder="Search..." />
          </div>
          <div className="header-actions">
            <button className="icon-btn"><i className="fas fa-bell"></i><span className="badge-dot"></span></button>
            <button className="icon-btn"><i className="fas fa-envelope"></i></button>
            <button className="icon-btn"><i className="fas fa-cog"></i></button>
          </div>
        </header>

        <div className="content-area">
          {renderContent()}
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}><i className="fas fa-times"></i></button>
            
            {/* View Enrollment Details */}
            {modalType === 'viewEnrollment' && selectedItem && (
              <div className="modal-body enrollment-details">
                <h2><i className="fas fa-file-signature"></i> Enrollment Application</h2>
                <div className="detail-grid">
                  <div className="detail-item"><label>Application ID:</label><span>#{selectedItem.id}</span></div>
                  <div className="detail-item"><label>Student Name:</label><span>{selectedItem.student_name}</span></div>
                  <div className="detail-item"><label>Father's Name:</label><span>{selectedItem.father_name}</span></div>
                  <div className="detail-item"><label>Date of Birth:</label><span>{selectedItem.date_of_birth}</span></div>
                  <div className="detail-item"><label>Gender:</label><span>{selectedItem.gender}</span></div>
                  <div className="detail-item"><label>Blood Group:</label><span>{selectedItem.blood_group || 'N/A'}</span></div>
                  <div className="detail-item"><label>Applying For:</label><span>Class {selectedItem.applying_for_class}</span></div>
                  <div className="detail-item"><label>Previous School:</label><span>{selectedItem.previous_school || 'N/A'}</span></div>
                  <div className="detail-item"><label>Phone:</label><span>{selectedItem.phone}</span></div>
                  <div className="detail-item"><label>Guardian Phone:</label><span>{selectedItem.guardian_phone || 'N/A'}</span></div>
                  <div className="detail-item full-width"><label>Address:</label><span>{selectedItem.address}</span></div>
                  <div className="detail-item"><label>Email:</label><span>{selectedItem.email || 'N/A'}</span></div>
                  <div className="detail-item"><label>Status:</label><span className={`badge ${selectedItem.status}`}>{selectedItem.status}</span></div>
                  <div className="detail-item"><label>Applied On:</label><span>{new Date(selectedItem.created_at).toLocaleDateString()}</span></div>
                </div>
                <div className="modal-actions">
                  <button className="btn-approve" onClick={() => handleUpdateEnrollmentStatus(selectedItem.id, 'approved')} disabled={actionLoading}>
                    <i className="fas fa-check"></i> Approve
                  </button>
                  <button className="btn-reject" onClick={() => handleUpdateEnrollmentStatus(selectedItem.id, 'rejected')} disabled={actionLoading}>
                    <i className="fas fa-times"></i> Reject
                  </button>
                </div>
              </div>
            )}
            
            {/* Add/Edit Student Form */}
            {(modalType === 'addStudent' || modalType === 'editStudent') && (
              <div className="modal-body">
                <h2><i className="fas fa-user-graduate"></i> {modalType === 'addStudent' ? 'Add New Student' : 'Edit Student'}</h2>
                <form onSubmit={handleSubmitStudent} className="modal-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Student ID *</label>
                      <input type="text" value={studentForm.student_id} onChange={(e) => setStudentForm({...studentForm, student_id: e.target.value})} required placeholder="e.g., STU001" />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>First Name *</label>
                      <input type="text" value={studentForm.first_name} onChange={(e) => setStudentForm({...studentForm, first_name: e.target.value})} required />
                    </div>
                    <div className="form-group">
                      <label>Last Name *</label>
                      <input type="text" value={studentForm.last_name} onChange={(e) => setStudentForm({...studentForm, last_name: e.target.value})} required />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Email *</label>
                      <input type="email" value={studentForm.email} onChange={(e) => setStudentForm({...studentForm, email: e.target.value})} required />
                    </div>
                    <div className="form-group">
                      <label>{modalType === 'addStudent' ? 'Password *' : 'Password (leave empty to keep)'}</label>
                      <input type="password" value={studentForm.password} onChange={(e) => setStudentForm({...studentForm, password: e.target.value})} required={modalType === 'addStudent'} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Grade *</label>
                      <select value={studentForm.grade} onChange={(e) => setStudentForm({...studentForm, grade: e.target.value})} required>
                        <option value="">Select Grade</option>
                        <option value="1">Grade 1</option>
                        <option value="2">Grade 2</option>
                        <option value="3">Grade 3</option>
                        <option value="4">Grade 4</option>
                        <option value="5">Grade 5</option>
                        <option value="6">Grade 6</option>
                        <option value="7">Grade 7</option>
                        <option value="8">Grade 8</option>
                        <option value="9">Grade 9</option>
                        <option value="10">Grade 10</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Section *</label>
                      <select value={studentForm.section} onChange={(e) => setStudentForm({...studentForm, section: e.target.value})} required>
                        <option value="">Select Section</option>
                        <option value="A">Section A</option>
                        <option value="B">Section B</option>
                        <option value="C">Section C</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Phone</label>
                      <input type="text" value={studentForm.phone} onChange={(e) => setStudentForm({...studentForm, phone: e.target.value})} />
                    </div>
                    <div className="form-group">
                      <label>Parent Name</label>
                      <input type="text" value={studentForm.parent_name} onChange={(e) => setStudentForm({...studentForm, parent_name: e.target.value})} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Parent Phone</label>
                      <input type="text" value={studentForm.parent_phone} onChange={(e) => setStudentForm({...studentForm, parent_phone: e.target.value})} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Address</label>
                    <textarea value={studentForm.address} onChange={(e) => setStudentForm({...studentForm, address: e.target.value})}></textarea>
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="btn-submit" disabled={actionLoading}>
                      {actionLoading ? 'Saving...' : (modalType === 'addStudent' ? 'Add Student' : 'Update Student')}
                    </button>
                  </div>
                </form>
              </div>
            )}
            
            {/* Add/Edit Faculty Form */}
                        {/* Assign Fee Modal */}
                        {modalType === 'assignFee' && showModal && (
                          <div className="modal-body">
                            <h2><i className="fas fa-file-invoice-dollar"></i> Assign Fee to Student</h2>
                            <form onSubmit={handleSubmitFee} className="modal-form">
                              <div className="form-row">
                                <div className="form-group">
                                  <label>Student ID</label>
                                  <input type="text" value={feeForm.student_id} disabled />
                                </div>
                              </div>
                              <div className="form-row">
                                <div className="form-group">
                                  <label>Fee Type *</label>
                                  <select value={feeForm.fee_type} onChange={(e) => setFeeForm({...feeForm, fee_type: e.target.value})} required>
                                    <option value="tuition">Tuition</option>
                                    <option value="exam">Exam</option>
                                    <option value="library">Library</option>
                                    <option value="transport">Transport</option>
                                    <option value="lab">Lab</option>
                                    <option value="sports">Sports</option>
                                    <option value="other">Other</option>
                                  </select>
                                </div>
                                <div className="form-group">
                                  <label>Amount *</label>
                                  <input type="number" value={feeForm.amount} onChange={(e) => setFeeForm({...feeForm, amount: e.target.value})} required placeholder="e.g., 50000" />
                                </div>
                              </div>
                              <div className="form-row">
                                <div className="form-group">
                                  <label>Due Date *</label>
                                  <input type="date" value={feeForm.due_date} onChange={(e) => setFeeForm({...feeForm, due_date: e.target.value})} required />
                                </div>
                                <div className="form-group">
                                  <label>Term *</label>
                                  <select value={feeForm.term} onChange={(e) => setFeeForm({...feeForm, term: e.target.value})} required>
                                    <option value="Term 1">Term 1</option>
                                    <option value="Term 2">Term 2</option>
                                    <option value="Term 3">Term 3</option>
                                  </select>
                                </div>
                              </div>
                              <div className="form-row">
                                <div className="form-group">
                                  <label>Academic Year *</label>
                                  <input type="text" value={feeForm.academic_year} onChange={(e) => setFeeForm({...feeForm, academic_year: e.target.value})} required />
                                </div>
                              </div>
                              <div className="form-group">
                                <label>Description</label>
                                <textarea value={feeForm.description} onChange={(e) => setFeeForm({...feeForm, description: e.target.value})}></textarea>
                              </div>
                              <div className="modal-actions">
                                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn-submit" disabled={actionLoading}>
                                  {actionLoading ? 'Saving...' : 'Assign Fee'}
                                </button>
                              </div>
                            </form>
                          </div>
                        )}
            {(modalType === 'addFaculty' || modalType === 'editFaculty') && (
              <div className="modal-body">
                <h2><i className="fas fa-chalkboard-teacher"></i> {modalType === 'addFaculty' ? 'Add New Faculty' : 'Edit Faculty'}</h2>
                <form onSubmit={handleSubmitFaculty} className="modal-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Faculty ID *</label>
                      <input type="text" value={facultyForm.faculty_id} onChange={(e) => setFacultyForm({...facultyForm, faculty_id: e.target.value})} required placeholder="e.g., FAC001" />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>First Name *</label>
                      <input type="text" value={facultyForm.first_name} onChange={(e) => setFacultyForm({...facultyForm, first_name: e.target.value})} required />
                    </div>
                    <div className="form-group">
                      <label>Last Name *</label>
                      <input type="text" value={facultyForm.last_name} onChange={(e) => setFacultyForm({...facultyForm, last_name: e.target.value})} required />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Email *</label>
                      <input type="email" value={facultyForm.email} onChange={(e) => setFacultyForm({...facultyForm, email: e.target.value})} required />
                    </div>
                    <div className="form-group">
                      <label>{modalType === 'addFaculty' ? 'Password *' : 'Password (leave empty to keep)'}</label>
                      <input type="password" value={facultyForm.password} onChange={(e) => setFacultyForm({...facultyForm, password: e.target.value})} required={modalType === 'addFaculty'} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Department *</label>
                      <select value={facultyForm.department} onChange={(e) => setFacultyForm({...facultyForm, department: e.target.value})} required>
                        <option value="">Select Department</option>
                        <option value="Science">Science</option>
                        <option value="Mathematics">Mathematics</option>
                        <option value="English">English</option>
                        <option value="Social Studies">Social Studies</option>
                        <option value="Computer Science">Computer Science</option>
                        <option value="Arts">Arts</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Designation *</label>
                      <select value={facultyForm.designation} onChange={(e) => setFacultyForm({...facultyForm, designation: e.target.value})} required>
                        <option value="">Select Designation</option>
                        <option value="Teacher">Teacher</option>
                        <option value="Senior Teacher">Senior Teacher</option>
                        <option value="Head of Department">Head of Department</option>
                        <option value="Vice Principal">Vice Principal</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Subject</label>
                      <input type="text" value={facultyForm.subject} onChange={(e) => setFacultyForm({...facultyForm, subject: e.target.value})} />
                    </div>
                    <div className="form-group">
                      <label>Phone</label>
                      <input type="text" value={facultyForm.phone} onChange={(e) => setFacultyForm({...facultyForm, phone: e.target.value})} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Qualification</label>
                    <input type="text" value={facultyForm.qualification} onChange={(e) => setFacultyForm({...facultyForm, qualification: e.target.value})} placeholder="e.g., M.Sc., B.Ed." />
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="btn-submit" disabled={actionLoading}>
                      {actionLoading ? 'Saving...' : (modalType === 'addFaculty' ? 'Add Faculty' : 'Update Faculty')}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Credentials Display Modal */}
            {modalType === 'credentials' && createdCredentials && (
              <div className="modal-body credentials-display">
                <h2><i className="fas fa-check-circle"></i> Account Created Successfully!</h2>
                <div className="credentials-card">
                  <div className="credentials-icon">✅</div>
                  <p className="credentials-type">{createdCredentials.type} Account</p>
                  <div className="credentials-details">
                    <div className="credential-row"><span>Name:</span><strong>{createdCredentials.name}</strong></div>
                    <div className="credential-row"><span>Email:</span><strong>{createdCredentials.email}</strong></div>
                    <div className="credential-row"><span>Password:</span><strong>{createdCredentials.password}</strong></div>
                    <div className="credential-row"><span>ID:</span><strong>{createdCredentials.id}</strong></div>
                  </div>
                  <p className="credentials-warning">
                    <i className="fas fa-exclamation-triangle"></i> Please save these credentials. The password cannot be recovered later.
                  </p>
                </div>
                <div className="modal-actions">
                  <button className="btn-submit" onClick={() => setShowModal(false)}>
                    <i className="fas fa-check"></i> Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <div className="toast-container">
        {toasts.map(toast => (
          <div key={toast.id} className={`toast toast-${toast.type}`}>
            <div className="toast-icon">
              <i className={`fas ${toast.type === 'success' ? 'fa-check-circle' : toast.type === 'error' ? 'fa-times-circle' : toast.type === 'warning' ? 'fa-exclamation-triangle' : 'fa-info-circle'}`}></i>
            </div>
            <span className="toast-message">{toast.message}</span>
            <button className="toast-close" onClick={() => dismissToast(toast.id)}><i className="fas fa-times"></i></button>
          </div>
        ))}
      </div>

      {/* Confirm Dialog */}
      {confirmDialog.show && (
        <div className="confirm-overlay" onClick={closeConfirmDialog}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className={`confirm-icon ${confirmDialog.type}`}>
              <i className={`fas ${confirmDialog.type === 'danger' ? 'fa-trash-alt' : 'fa-exclamation-triangle'}`}></i>
            </div>
            <h3 className="confirm-title">{confirmDialog.title}</h3>
            <p className="confirm-message">{confirmDialog.message}</p>
            <div className="confirm-actions">
              <button className="confirm-btn cancel" onClick={closeConfirmDialog}>Cancel</button>
              <button className={`confirm-btn ${confirmDialog.type}`} onClick={confirmDialog.onConfirm}>
                {confirmDialog.type === 'danger' ? 'Delete' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Dashboard;
